import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

import { Navbar } from "../../Common/Navbar/Navbar";
import {
  fetchDynamicQuestionnaire,
  submitDynamicQuestionnaire,
} from "./api";
import { setOrderData, fetchQuestionAnswer } from "../questionnaire.slice";
import { FieldRenderer } from "./FieldRenderer";
import "./Dynamic.css";

// Build the {questionId: initialValue} map from a list of section payloads.
const initialAnswersFromSections = (sections) => {
  const out = {};
  for (const sec of sections || []) {
    for (const q of sec.questions || []) {
      out[q.id] = q.answer ?? null;
    }
  }
  return out;
};

const flattenQuestions = (sections) =>
  (sections || []).flatMap((s) => s.questions || []);

const isAnswered = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    return Object.values(value).some((v) => {
      if (Array.isArray(v)) return v.length > 0;
      return v !== null && v !== undefined && v !== "";
    });
  }
  return true;
};

const SURPRISE_SENTINEL = "surprise";

// Word counter helper used by max_words validation. Mirrors the legacy
// `countWords` behavior — splits on whitespace and ignores empty fragments.
export const countWords = (s) => {
  if (!s || typeof s !== "string") return 0;
  return s.trim().split(/\s+/).filter(Boolean).length;
};

// Per-question validation. Returns null if OK, or an i18n error message.
const validateAnswer = (q, value, lang) => {
  // "surprise" sentinel always passes — the user has opted out.
  if (value === SURPRISE_SENTINEL) return null;

  // Required
  if (q.required && !isAnswered(value)) {
    return lang === "ar" ? "هذا السؤال مطلوب" : "This question is required.";
  }
  if (!isAnswered(value)) return null; // optional & empty → OK

  // Max words (text/textarea)
  if (q.max_words && typeof value === "string") {
    if (countWords(value) > q.max_words) {
      return lang === "ar"
        ? `تجاوزت ${q.max_words} كلمة`
        : `Exceeds ${q.max_words}-word limit.`;
    }
  }

  // Custom regex
  if (q.validation_regex && typeof value === "string") {
    try {
      const re = new RegExp(q.validation_regex);
      if (!re.test(value)) {
        return (
          (lang === "ar" && q.validation_message_arabic) ||
          q.validation_message ||
          (lang === "ar" ? "إجابة غير صالحة" : "Invalid answer.")
        );
      }
    } catch (_e) {
      // bad pattern shipped by admin — let it through rather than block user
    }
  }

  return null;
};

export const DynamicQuestionnaire = ({ lang, setLang }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orderId: orderIdParam } = useParams();

  const orderId =
    orderIdParam ||
    (typeof window !== "undefined" && localStorage.getItem("order_id")) ||
    null;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sections, setSections] = useState([]);
  const [answers, setAnswers] = useState({});
  // qid -> error message string (or absence = no error)
  const [errors, setErrors] = useState({});
  const [pageIdx, setPageIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!orderId) {
        setError("No order found.");
        setLoading(false);
        return;
      }
      try {
        const res = await fetchDynamicQuestionnaire(orderId);
        if (cancelled) return;
        const secs = res?.sections || [];
        setSections(secs);
        setAnswers(initialAnswersFromSections(secs));
        if (res?.order) dispatch(setOrderData(res.order));
        dispatch(fetchQuestionAnswer(flattenQuestions(secs)));
      } catch (e) {
        if (!cancelled) setError("Could not load questionnaire.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [orderId, dispatch]);

  const setAnswer = (qid, v) => {
    setAnswers((prev) => ({ ...prev, [qid]: v }));
    // Clear any existing error on this field as soon as the user touches it.
    setErrors((prev) => {
      if (!prev[qid]) return prev;
      const next = { ...prev };
      delete next[qid];
      return next;
    });
  };

  const setSurprise = (qid) => setAnswer(qid, SURPRISE_SENTINEL);

  const allQuestions = useMemo(() => flattenQuestions(sections), [sections]);
  const totalPages = sections.length;
  const currentSection = sections[pageIdx] || null;

  const progress = useMemo(() => {
    if (!allQuestions.length) return 0;
    const done = allQuestions.filter((q) => isAnswered(answers[q.id])).length;
    return Math.round((done / allQuestions.length) * 100);
  }, [allQuestions, answers]);

  // Validate just the current section before allowing forward navigation;
  // validate everything before submit. Honors max_words, regex, surprise.
  const validate = (section) => {
    const next = {};
    const list = section ? section.questions : allQuestions;
    for (const q of list) {
      const err = validateAnswer(q, answers[q.id], lang);
      if (err) next[q.id] = err;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const goNext = () => {
    if (!validate(currentSection)) {
      toast.error(
        lang === "ar"
          ? "يرجى الإجابة على جميع الأسئلة المطلوبة في هذه الصفحة."
          : "Please answer the required questions on this page."
      );
      return;
    }
    if (pageIdx < totalPages - 1) {
      setPageIdx(pageIdx + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      handleSave(true);
    }
  };

  const goBack = () => {
    if (pageIdx > 0) {
      setPageIdx(pageIdx - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSave = async (submit = false) => {
    if (submit && !validate(null)) {
      toast.error(
        lang === "ar"
          ? "يرجى الإجابة على جميع الأسئلة المطلوبة."
          : "Please answer every required question."
      );
      return;
    }
    setSubmitting(true);
    try {
      await submitDynamicQuestionnaire(
        orderId,
        answers,
        submit ? "submit" : "save"
      );
      if (submit) {
        toast.success(
          lang === "ar" ? "تم إرسال الاستبيان" : "Questionnaire submitted"
        );
        navigate("/dashboard");
      } else {
        toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      }
    } catch (e) {
      toast.error(
        lang === "ar" ? "حدث خطأ، حاول مجددًا" : "Something went wrong"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar isLang={lang} setIsLang={setLang} />
        <div className="dq-loading">Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div>
        <Navbar isLang={lang} setIsLang={setLang} />
        <div className="dq-empty">{error}</div>
      </div>
    );
  }
  if (!sections.length || !allQuestions.length) {
    return (
      <div>
        <Navbar isLang={lang} setIsLang={setLang} />
        <div className="dq-empty">
          {lang === "ar"
            ? "لا توجد أسئلة لهذا الطلب."
            : "No questions configured for this package yet."}
        </div>
      </div>
    );
  }

  const isLastPage = pageIdx === totalPages - 1;
  const sectionTitle =
    lang === "ar" && currentSection.title_arabic
      ? currentSection.title_arabic
      : currentSection.title_english;
  const accent = currentSection.background_color || "#000000";

  return (
    <div>
      <Navbar isLang={lang} setIsLang={setLang} />
      <div
        className="dq-wrap"
        dir={lang === "ar" ? "rtl" : "ltr"}
        style={
          currentSection.background_color
            ? { background: currentSection.background_color, paddingTop: 0 }
            : {}
        }
      >
        {/* Hero / stage header — mirrors the legacy 7-page UI */}
        <div className="dq-hero">
          {currentSection.image ? (
            <img className="dq-hero-img" src={currentSection.image} alt="" />
          ) : null}
          <h1 className="dq-hero-title">{sectionTitle}</h1>
          <div className="dq-hero-meta">
            {lang === "ar"
              ? `الصفحة ${pageIdx + 1} من ${totalPages}`
              : `Page ${pageIdx + 1} of ${totalPages}`}
          </div>
        </div>

        <div className="dq-progress">
          <span>
            {lang === "ar" ? "التقدم" : "Progress"}: {progress}%
          </span>
          <div className="dq-progress-bar">
            <div className="dq-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {currentSection.questions.map((q) => {
          const text =
            lang === "ar" && q.question_arabic ? q.question_arabic : q.question;
          const desc =
            lang === "ar" && q.description_arabic
              ? q.description_arabic
              : q.description;
          const value = answers[q.id];
          const isSurprised = value === SURPRISE_SENTINEL;
          const wordCount =
            q.max_words && typeof value === "string" ? countWords(value) : 0;
          const overLimit = q.max_words && wordCount > q.max_words;

          return (
            <div className="dq-card" key={q.id}>
              {q.max_words ? (
                <div
                  style={{
                    textAlign: "right",
                    fontSize: 12,
                    color: overLimit ? "#e54848" : "#888",
                    marginBottom: 4,
                  }}
                >
                  {lang === "ar" ? "كلمات" : "Words"}: {wordCount} / {q.max_words}
                </div>
              ) : null}
              <h3>
                {text}
                {q.required ? <span className="dq-required">*</span> : null}
              </h3>
              {desc ? <p className="dq-desc">{desc}</p> : null}
              {q.image ? (
                <img className="dq-question-thumb" src={q.image} alt="" />
              ) : null}

              {isSurprised ? (
                <div
                  style={{
                    padding: 12,
                    background: "#fff7d6",
                    borderRadius: 6,
                    color: "#7a5e00",
                    fontSize: 14,
                  }}
                >
                  {lang === "ar"
                    ? "ستقوم لجنة التصميم بالاختيار نيابة عنك."
                    : "The design team will pick this for you."}{" "}
                  <button
                    type="button"
                    style={{
                      marginInlineStart: 12,
                      background: "transparent",
                      border: "none",
                      color: "#7a5e00",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                    onClick={() => setAnswer(q.id, null)}
                  >
                    {lang === "ar" ? "تراجع" : "Undo"}
                  </button>
                </div>
              ) : (
                <FieldRenderer
                  question={q}
                  value={value}
                  onChange={(v) => setAnswer(q.id, v)}
                  lang={lang}
                />
              )}

              {q.allow_surprise && !isSurprised ? (
                <div style={{ textAlign: "center", marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                    {lang === "ar"
                      ? "غير متأكد؟ لا بأس!"
                      : "Not sure? It's okay!"}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSurprise(q.id)}
                    style={{
                      padding: "6px 18px",
                      border: "1px solid #ccc",
                      background: "#fff",
                      borderRadius: 4,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {lang === "ar" ? "فاجئني!" : "SURPRISE ME!"}
                  </button>
                </div>
              ) : null}

              {errors[q.id] ? (
                <div className="dq-error">{errors[q.id]}</div>
              ) : null}
            </div>
          );
        })}

        <div className="dq-actions">
          <button
            type="button"
            className="dq-btn dq-btn-secondary"
            onClick={goBack}
            disabled={pageIdx === 0 || submitting}
          >
            {lang === "ar" ? "العودة" : "Back"}
          </button>
          <button
            type="button"
            className="dq-btn dq-btn-secondary"
            onClick={() => handleSave(false)}
            disabled={submitting}
          >
            {lang === "ar" ? "احفظ لوقت لاحق" : "Save for later"}
          </button>
          <button
            type="button"
            className="dq-btn dq-btn-primary"
            onClick={goNext}
            disabled={submitting}
            style={{ background: accent, color: "#fff" }}
          >
            {isLastPage
              ? lang === "ar"
                ? "إرسال"
                : "Submit"
              : lang === "ar"
              ? "التالي"
              : "Next"}
          </button>
        </div>

        {/* Visual stepper — mirrors the legacy 7-stage navigator with
            a paper-airplane indicator over the current section. */}
        <nav className="dq-stepper" aria-label="Questionnaire progress">
          {sections.map((s, idx) => {
            const t =
              lang === "ar" && s.title_arabic ? s.title_arabic : s.title_english;
            return (
              <button
                type="button"
                key={s.id || idx}
                className={"dq-stepper-item" + (idx === pageIdx ? " current" : "")}
                onClick={() => setPageIdx(idx)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <span className="dq-step-mark" aria-hidden>*</span>
                <span className="dq-step-label">{t}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default DynamicQuestionnaire;
